import mixpanel from "mixpanel-browser";
import ReactGA from "react-ga4";
import packageJson from "../../package.json";

const APP_VERSION = packageJson.version;

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.mixpanelToken =
      process.env.REACT_APP_MIXPANEL_TOKEN ||
      "faee73412ffe435ae338bf18e9605b6d";
  }

  init() {
    if (this.isInitialized) return;

    try {
      if (this.mixpanelToken) {
        mixpanel.init(this.mixpanelToken, {
          debug: process.env.NODE_ENV === "development",
          track_pageview: false,
          persistence: "localStorage",
          property_blacklist: [
            "$current_url",
            "$initial_referrer",
            "$referrer",
          ],
          batch_requests: true,
          secure_cookie: true,
        });
        mixpanel.register({
          flow: "nationwide",
          app_version: APP_VERSION,
        });
        console.log("✅ Mixpanel initialized");
      } else {
        console.warn(
          "⚠️ REACT_APP_MIXPANEL_TOKEN not set — Mixpanel tracking disabled."
        );
      }

      this.isInitialized = true;
    } catch (error) {
      console.error("❌ Analytics initialization failed:", error);
      this.trackError("analytics_init_failed", error);
    }
  }

  trackPageView(page, properties = {}) {
    try {
      const pageData = {
        page,
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        timestamp: new Date().toISOString(),
        ...properties,
      };

      ReactGA.send({
        hitType: "pageview",
        page,
        ...properties,
      });

      if (this.isMixpanelAvailable()) {
        mixpanel.track(page, pageData);
      }

      if (process.env.NODE_ENV === "development") {
        console.log("📊 Page view:", page);
      }
    } catch (error) {
      console.error("❌ Page view tracking failed:", error);
    }
  }

  trackEvent(eventName, properties = {}) {
    try {
      const eventData = {
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        screen: this.getScreenFromPath(window.location.pathname),
        ...properties,
      };

      ReactGA.event({
        action: eventName,
        category: properties.category || "User Interaction",
        label: properties.label,
        value: properties.value,
      });

      if (this.isMixpanelAvailable()) {
        mixpanel.track(eventName, eventData);
      }

      if (process.env.NODE_ENV === "development") {
        console.log("📊 Event:", eventName, eventData);
      }
    } catch (error) {
      console.error("❌ Event tracking failed:", error);
    }
  }

  identifyUser(userId, userProperties = {}) {
    try {
      ReactGA.set({ userId });

      if (this.isMixpanelAvailable()) {
        mixpanel.identify(userId);
        mixpanel.people.set({
          $name: userProperties.name,
          $email: userProperties.email,
          $created: userProperties.createdAt || new Date().toISOString(),
          ...userProperties,
        });
        mixpanel.register({ user_id: userId });
      }

      if (process.env.NODE_ENV === "development") {
        console.log("👤 Identified:", userId);
      }
    } catch (error) {
      console.error("❌ User identification failed:", error);
    }
  }

  peopleSet(userProperties = {}) {
    try {
      if (this.isMixpanelAvailable()) {
        mixpanel.people.set(userProperties);
      }
    } catch (error) {
      console.error("❌ peopleSet failed:", error);
    }
  }

  trackError(errorName, error, additionalContext = {}) {
    try {
      const errorData = {
        error_name: errorName,
        error_message: error?.message || error,
        error_stack: error?.stack,
        error_type: error?.constructor?.name || typeof error,
        url: window.location.href,
        pathname: window.location.pathname,
        screen: this.getScreenFromPath(window.location.pathname),
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        ...additionalContext,
      };

      ReactGA.event({
        action: "exception",
        category: "Error",
        label: errorName,
        description: error?.message || error,
        fatal: additionalContext.fatal || false,
      });

      if (this.isMixpanelAvailable()) {
        mixpanel.track("nw_error", errorData);
      }

      console.error("🚨 Error tracked:", errorName, errorData);
    } catch (trackingError) {
      console.error("❌ Error tracking failed:", trackingError);
    }
  }

  isMixpanelAvailable() {
    return Boolean(mixpanel && this.mixpanelToken);
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem("analytics_session_id");
    if (!sessionId) {
      sessionId =
        "session_" + Date.now() + "_" + Math.random().toString(36).slice(2, 11);
      sessionStorage.setItem("analytics_session_id", sessionId);
    }
    return sessionId;
  }

  getScreenFromPath(pathname) {
    if (!pathname) return "unknown";
    const trimmed = pathname.replace(/^\/+|\/+$/g, "");
    return trimmed || "root";
  }

  resetUser() {
    try {
      if (this.isMixpanelAvailable()) {
        mixpanel.reset();
        mixpanel.register({
          flow: "nationwide",
          app_version: APP_VERSION,
        });
      }
      sessionStorage.removeItem("analytics_session_id");
    } catch (error) {
      console.error("❌ User reset failed:", error);
    }
  }
}

const analyticsService = new AnalyticsService();

export default analyticsService;
